import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { MaterialReactTable } from "material-react-table";
import type {
  PaginationState,
  ColumnFiltersState,
  SortingState,
} from "@tanstack/react-table";
import { useForm } from "react-hook-form";
import _ from "lodash";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { handleSearchData, paginationQuery } from "../../utils/GlobalHelper";
import { ServiceManagementListAction } from "../../redux/Action/DashboardAction";
import { Config } from "../../config/Config";
import {
  dateFormat,
  encodeID,
  handleShowSwalConfirmation,
  sidebarOpen,
} from "../../utils/Helper";
import Button from "../../components/Ui/Button";
import { MRT_Localization_AL, PAGES } from "../../constants/Constants";
import { BillingManagementListAction } from "../../redux/Action/BillingAction";
import BillingManagementFilter from "../../components/Model/BillingManagementFilter";

const BillingManagement = () => {
  const intl = useIntl();

  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const BookingManagementList = useSelector(
    (state: any) => state.BillingManagementListState,
  );

  // BillingManagementListState
  const [data, setData] = useState<any>();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: PAGES?.pageIndex,
    pageSize: PAGES?.pageSize,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowCount, setRowCount] = useState(0);
  const [totalNetRevenue, setTotalNetRevenue] = useState<number | null>(null);
  const [searchValue, setSearchValue]: any = useState("");
  const [isError, setIsError] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disableDate, setDisableDate] = useState(true);

  const role_id = Config?.USER_DETAILS?.role_id;
  useEffect(() => {
    const fetchData = async () => {
      if (!data?.length) {
        setLoading(true);
      } else {
        setIsRefetching(true);
      }
      try {
        let query: any = paginationQuery(
          pagination.pageIndex,
          pagination.pageSize,
          sorting,
          searchValue,
          columnFilters,
        );
        await dispatch(
          BillingManagementListAction({
            ...query,
            role_id: Config?.USER_DETAILS?.role_id,
          }),
        );
      } catch (error) {
        setIsError(true);
        return;
      }
      setIsError(false);
      setLoading(false);
      setIsRefetching(false);
    };
    if (
      columnFilters ||
      pagination.pageIndex ||
      pagination.pageSize ||
      sorting
    ) {
      fetchData();
    }
  }, [columnFilters, pagination.pageIndex, pagination.pageSize, sorting]);

  useEffect(() => {
    if (BookingManagementList?.status) {
      setRowCount(BookingManagementList?.data?.data?.count);
      setData(BookingManagementList?.data?.data?.rows);
      const amount = BookingManagementList?.data?.data?.totalAmount;
      setTotalNetRevenue(amount != null && amount !== undefined ? Number(amount) : null);
    }
  }, [BookingManagementList?.data]);

  const handleStatus: any = (id: any, status: any) => {
    // dispatch(ServiceManagementStatusAction({ status_id: id, status: status }))
  };

  const columns: any = [
    {
      accessorKey: "S.NO",
      header: <FormattedMessage id="sno" />,
      accessorFn: (row: Object, index: number) => {
        return index + 1;
      },
      enableSorting: false,
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
      size: 80,
    },
    {
      accessorKey: "booking_id",
      header: <FormattedMessage id="booking_id" />,
      enableSorting: false,
      accessorFn: (row: any) => {
        return row.booking_id;
      },
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
      size: 150,
    },
    {
      accessorKey: "duration",
      header: <FormattedMessage id="customer_name" />,
      enableSorting: false,
      accessorFn: (row: any) => {
        return row?.user_details?.name ? row?.user_details?.name : "N/A";
      },
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
      size: 150,
    },
    {
      accessorKey: "price",
      header: <FormattedMessage id="service_name" />,
      enableSorting: false,
      accessorFn: (row: any) => {
        return row?.service_details?.name ? row?.service_details?.name : "N/A";
      },
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
      size: 150,
    },
    {
      accessorKey: "amount",
      header: <FormattedMessage id="booking_amount" />,
      enableSorting: false,

      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
      size: 150,
    },
    {
      accessorKey: "category",
      header: <FormattedMessage id="booking_type" />,
      enableSorting: false,
      accessorFn: (row: any) => {
        return row?.booking_status
          ? row?.booking_status == 1
            ? "Online"
            : "Offline"
          : "N/A";
      },
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
      size: 150,
    },
    {
      accessorKey: "created_at",
      // header:'creation_date' ,
      header: <FormattedMessage id="Transaction_created_on" />,
      accessorFn: (row: any) => {
        return (
          <>
            <td>{dateFormat(row?.booking_date)}</td>
          </>
        );
      },
      enableSorting: false,
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
      size: 150,
    },

    {
      header: <FormattedMessage id="action" />,
      size: 150,
      accessorFn: (row: {
        id: any;
        status: number;
        booking_id: any;
        service_details: any;
      }) => (
        <>
          <div className="userAction">
            {/* <Link to="#" className='btn btnroundIcon'><i className="icon-view"></i></Link> */}
            <Link
              to={`/billing-management-view/${encodeID(row?.booking_id)}/${encodeID(row?.service_details?.id)}`}
              className="btn btnroundIcon"
            >
              <i className="icon-view"></i>
            </Link>
          </div>
        </>
      ),
      enableSorting: false,
      enableColumnOrdering: false,
      enableColumnDragging: false,
      enableColumnFilter: false,
    },
  ];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      start_date: "",
      end_date: "",
      searchValue: "",
      booking_type: "",
    },
  }) as any;

  const [searched, setSearched] = useState<string>("");

  const onSubmit = (payload: any) => {
    const columnFilters = [
      { id: "start_date", value: payload?.start_date },
      { id: "end_date", value: payload?.end_date },
      { id: "booking_type", value: String(payload?.booking_type) },
    ];
    setColumnFilters(columnFilters);
  };

  const handleReset = () => {
    reset();
    setSearched("");
    setColumnFilters([]);
  };

  const requestSearch = useCallback(
    _.debounce((searchedVal: string) => {
      if (searchedVal.length > 0) {
        setSearched(searchedVal);

        const filteredRows =
          data &&
          data.filter((row: any) => {
            return row?.name?.toLowerCase().includes(searchedVal.toLowerCase());
          });
        const columnFilters = [
          { id: "start_date", value: "" },
          { id: "end_date", value: "" },
          { id: "search", value: searchedVal },
        ];

        const dataData = [{ id: "search", value: searchedVal }];
        setColumnFilters(dataData);
      } else {
        setColumnFilters([]);
      }
    }, 1000), // 300 milliseconds debounce time
    [],
  );

  const handleSearchChange = (searchVal: any) => {
    const value = searchVal?.target?.value.replace(/\s{2,}/g, " ");

    setSearched(value);
    requestSearch(value.trim());
  };

  const handleFilter = () => {
    // dispatch(ProfileGetCategoryAction({}));
  };

  useEffect(() => {
    return () => {
      dispatch(BillingManagementListAction("RESET"));
    };
  }, []); // Reset data

  return (
    <>
      <div className="col-lg-9 serviceManagement">
        <div className="dashboard bg-white">
          <div className="dashboardTop d-sm-flex align-items-center">
            <h2>
              <button
                type="button"
                onClick={() => {
                  sidebarOpen();
                }}
                className="btn mobileMenu p-1 d-lg-none"
              >
                <span className="icon-menu-right"></span>
              </button>{" "}
              {<FormattedMessage id="billing_management" />}
            </h2>
            <div className="filterBTN d-none d-sm-flex">
              <div className="search">
                <span className="icon-search"></span>
                <input
                  type="search"
                  className="form-control"
                  placeholder={intl.formatMessage({ id: "search" })}
                  value={searched} // Removed .trim() to allow spaces
                  onChange={handleSearchChange}
                />{" "}
                {/* <input type='search' className='form-control' placeholder={intl.formatMessage({ id: 'search' })} value={searched.trim()} onChange={handleSearchChange} /> */}
              </div>

              <button
                type="button"
                className="btn btn-fill filterlist btn-sm"
                data-bs-toggle="modal"
                data-bs-target="#servicelistFilter"
                onClick={handleFilter}
              >
                <i className="icon-filter"></i> <FormattedMessage id="filter" />
              </button>
              <button
                type="button"
                className="btn btn-sm-outline"
                onClick={handleReset}
              >
                <FormattedMessage id="reset" />
              </button>
            </div>

            <div className="filterBTN d-sm-none">
              <div className="row gy-2">
                <div className="col-md-12">
                  <div className="search">
                    <span className="icon-search"></span>
                    <input
                      type="search"
                      className="form-control  w-100"
                      placeholder="Search"
                    />
                  </div>
                </div>
                <div className="col-6">
                  <button
                    type="button"
                    className="btn btn-fill filterlist btn-sm w-100"
                    data-bs-toggle="modal"
                    data-bs-target="#servicelistFilter"
                  >
                    <i className="icon-filter"></i> Filter
                  </button>
                </div>
                <div className="col-6">
                  {role_id != 2 && (
                    <Button
                      type="button"
                      text={"+" + intl.formatMessage({ id: "add_new_service" })}
                      className="btn btn-sm-outline"
                      onClick={() => navigate("/service")}
                    />
                  )}

                  {/* <button type='button' className='btn btn-sm-outline  w-100'>+{<FormattedMessage id="add_new_service" />}</button> */}
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center mb-3 mt-3 px-0 ms-3">
            <span className="fw-semibold me-2">
              <FormattedMessage id="total_net_revenue" defaultMessage="Total Net Revenue" />:
            </span>
            <span className="fs-5">
              {totalNetRevenue != null
                ? Number(totalNetRevenue).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "—"}
            </span>
          </div>
          <div className="dashboarcard p-0">
            {
              <MaterialReactTable
                columns={columns}
                data={data || []}
                enableGlobalFilter={false}
                enableHiding={false}
                enableColumnFilters={false}
                enableDensityToggle={false}
                enableColumnActions={false}
                enableFullScreenToggle={false}
                positionToolbarAlertBanner="top"
                // localization={MRT_Localization_AL}

                initialState={{
                  showColumnFilters: true,
                  globalFilter: searchValue, // Set the default search value here
                }}
                manualFiltering
                manualPagination
                manualSorting
                muiToolbarAlertBannerProps={
                  isError
                    ? { color: "error", children: "Error loading data" }
                    : undefined
                }
                muiSearchTextFieldProps={{
                  placeholder: "Search from name",
                }}
                onColumnFiltersChange={setColumnFilters}
                onGlobalFilterChange={(value: any) =>
                  handleSearchData(
                    value,
                    setIsError,
                    setLoading,
                    setIsRefetching,
                    setSearchValue,
                  )
                }
                onPaginationChange={setPagination}
                onSortingChange={setSorting}
                rowCount={rowCount}
                state={{
                  columnFilters,
                  isLoading: loading,
                  pagination,
                  showAlertBanner: isError,
                  showProgressBars: isRefetching,
                  sorting,
                }}
              />
            }
          </div>
        </div>
      </div>
      <BillingManagementFilter
        register={register}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        handleReset={handleReset}
        setValue={setValue}
        watch={watch}
        errors={errors}
        setDisableDate={setDisableDate}
        disableDate={disableDate}
      />
    </>
  );
};
export default BillingManagement;
